/**
 * Named theme palettes.
 *
 * A theme is a small set of **base** colours. It is deliberately not a full
 * dump of every CSS variable the app declares, for one reason: the surface and
 * border rungs of every colour family are `color-mix()` derivations of their
 * base in `index.css`, and they must stay that way. A theme that recolours
 * `--warning` gets a correct 8%/16% surface and a 30%/40% border for free, and
 * cannot accidentally ship a tint that has drifted from the colour it is meant
 * to be tinting. `-foreground` is supplied per family because it carries the
 * AA correction, which is not derivable from the base.
 *
 * That constraint is what keeps `designTokenGuard` meaningful under theming: a
 * theme may change a hue, it may not break the contrast contract or leave a
 * rung missing.
 *
 * @module
 */
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

/**
 * The default. It is not a palette — it is the absence of one.
 *
 * Selecting it applies **no** custom properties at all, so the app renders
 * exactly what `index.css` declares, including Forge's warm orange primary.
 * Themes are opt-in, and the shipped identity is what you get until you opt in.
 */
export const DEFAULT_THEME_ID = "forge";

/**
 * Roles a theme may set, named for the Forge tokens they drive rather than for
 * an abstract colour vocabulary — a theme author edits the thing whose name
 * they see in the DOM.
 *
 * Absent by design: every `-surface` and `-border` rung (derived), the eight
 * categorical hues (identity, not palette — a "Blocked" chip should not change
 * meaning because someone picked a warmer theme), and the geometry tokens.
 */
export const THEME_ROLES = [
  // Surface ladder
  "background",
  "foreground",
  "card",
  "cardForeground",
  "popover",
  "popoverForeground",
  // Chrome
  "sidebar",
  "sidebarForeground",
  "sidebarBorder",
  // Interaction
  "primary",
  "primaryForeground",
  "secondary",
  "secondaryForeground",
  "muted",
  "mutedForeground",
  "accent",
  "accentForeground",
  "border",
  "input",
  // Severity bases only. The ladder derives the rest.
  "destructive",
  "destructiveForeground",
  "warning",
  "warningForeground",
  "success",
  "successForeground",
  "info",
  "infoForeground",
] as const;

export type ThemeRole = (typeof THEME_ROLES)[number];

/** The two halves every palette carries. Upstream's name, adopted verbatim. */
export type ThemeAppearance = "light" | "dark";

/** The CSS custom property each role drives. */
export const THEME_ROLE_VARIABLES: Readonly<Record<ThemeRole, string>> = {
  accent: "--accent",
  accentForeground: "--accent-foreground",
  background: "--background",
  border: "--border",
  card: "--card",
  cardForeground: "--card-foreground",
  destructive: "--destructive",
  destructiveForeground: "--destructive-foreground",
  foreground: "--foreground",
  info: "--info",
  infoForeground: "--info-foreground",
  input: "--input",
  muted: "--muted",
  mutedForeground: "--muted-foreground",
  popover: "--popover",
  popoverForeground: "--popover-foreground",
  primary: "--primary",
  primaryForeground: "--primary-foreground",
  secondary: "--secondary",
  secondaryForeground: "--secondary-foreground",
  sidebar: "--sidebar",
  sidebarBorder: "--sidebar-border",
  sidebarForeground: "--sidebar-foreground",
  success: "--success",
  successForeground: "--success-foreground",
  warning: "--warning",
  warningForeground: "--warning-foreground",
};

export type ThemeColors = Readonly<Partial<Record<ThemeRole, string>>>;

/**
 * The roles a palette must define to be a palette at all.
 *
 * Everything else is optional and falls through to the shipped tokens when a
 * palette has no opinion. But one that does not repaint the canvas, the text on
 * it, or the rail is not a theme — it is a tint, and it would look broken
 * against the surfaces it failed to move.
 */
export const REQUIRED_THEME_ROLES = [
  "background",
  "foreground",
  "card",
  "sidebar",
  "primary",
  "border",
] as const satisfies readonly ThemeRole[];

export interface ThemePalette {
  readonly id: string;
  readonly name: string;
  /** One line, shown under the name in the picker. */
  readonly description: string;
  readonly light: ThemeColors;
  readonly dark: ThemeColors;
}

/**
 * The palettes.
 *
 * Every value is OKLCH, and deliberately at the precision it was authored with
 * rather than rounded: perceptual lightness is the axis the surface ladder is
 * built on, and the rungs of these ramps sit close enough together that
 * rounding visibly flattens them.
 *
 * A palette may omit any role. Omitted roles are simply not overridden and fall
 * through to the shipped tokens — which is why none of these define `success`
 * or `info`: the source palettes have no opinion on those two, and inventing
 * values would be fiction dressed as a port.
 */
const grove: ThemePalette = {
  dark: {
    accent: "oklch(0.437021 0.060312 158.962)",
    accentForeground: "oklch(0.990339 0.008411 325.64)",
    background: "oklch(0.260865 0.02152 162.75)",
    border: "oklch(0.457475 0.044046 160.971)",
    card: "oklch(0.363192 0.016572 165.32)",
    cardForeground: "oklch(0.990339 0.008411 325.64)",
    destructive: "oklch(0.655108 0.221148 23.473)",
    destructiveForeground: "oklch(0.704237 0.187511 22.228)",
    foreground: "oklch(0.990339 0.008411 325.64)",
    input: "oklch(0.519849 0.049896 160.863)",
    muted: "oklch(0.339728 0.039456 160.274)",
    mutedForeground: "oklch(0.715427 0.010896 171.428)",
    popover: "oklch(0.411828 0.014378 166.627)",
    popoverForeground: "oklch(0.990339 0.008411 325.64)",
    primary: "oklch(0.796228 0.133058 157.319)",
    primaryForeground: "oklch(0.222003 0.03479 328.979)",
    secondary: "oklch(0.380487 0.048313 159.608)",
    secondaryForeground: "oklch(0.990339 0.008411 325.64)",
    sidebar: "oklch(0.309925 0.032827 160.944)",
    sidebarBorder: "oklch(0.569253 0.015933 167.062)",
    sidebarForeground: "oklch(0.990339 0.008411 325.64)",
    warning: "oklch(0.772406 0.172798 65.367)",
    warningForeground: "oklch(0.829017 0.171221 81.038)",
  },
  description: "Mossy greens over a warm paper canvas.",
  id: "grove",
  light: {
    accent: "oklch(0.909438 0.021521 164.612)",
    accentForeground: "oklch(0.222003 0.03479 328.979)",
    background: "oklch(0.972369 0.005497 157.15)",
    border: "oklch(0.864831 0.01312 167.255)",
    card: "oklch(0.949276 0.004496 159.002)",
    cardForeground: "oklch(0.222003 0.03479 328.979)",
    destructive: "oklch(0.637823 0.237287 25.436)",
    destructiveForeground: "oklch(0.509494 0.208583 28.513)",
    foreground: "oklch(0.222003 0.03479 328.979)",
    input: "oklch(0.829746 0.016084 168.234)",
    muted: "oklch(0.945455 0.012308 162.879)",
    mutedForeground: "oklch(0.527266 0.012309 320.683)",
    popover: "oklch(0.932695 0.003778 160.944)",
    popoverForeground: "oklch(0.222003 0.03479 328.979)",
    primary: "oklch(0.523295 0.112292 158.089)",
    primaryForeground: "oklch(0.990339 0.008411 325.64)",
    secondary: "oklch(0.936464 0.014601 163.554)",
    secondaryForeground: "oklch(0.222003 0.03479 328.979)",
    sidebar: "oklch(0.936464 0.014601 163.554)",
    sidebarBorder: "oklch(0.860274 0.010287 168.339)",
    sidebarForeground: "oklch(0.222003 0.03479 328.979)",
    warning: "oklch(0.772406 0.172798 65.367)",
    warningForeground: "oklch(0.545036 0.155019 45.359)",
  },
  name: "Grove",
};

const ocean: ThemePalette = {
  dark: {
    accent: "oklch(0.413315 0.051874 243.855)",
    accentForeground: "oklch(0.990339 0.008411 325.64)",
    background: "oklch(0.242641 0.024125 250.573)",
    border: "oklch(0.438653 0.039496 245.44)",
    card: "oklch(0.348439 0.019942 253.696)",
    cardForeground: "oklch(0.990339 0.008411 325.64)",
    destructive: "oklch(0.655108 0.221148 23.473)",
    destructiveForeground: "oklch(0.702184 0.189226 22.228)",
    foreground: "oklch(0.990339 0.008411 325.64)",
    input: "oklch(0.500905 0.043574 244.781)",
    muted: "oklch(0.319287 0.036766 246.065)",
    mutedForeground: "oklch(0.691936 0.016294 261.588)",
    popover: "oklch(0.398517 0.018232 255.72)",
    popoverForeground: "oklch(0.990339 0.008411 325.64)",
    primary: "oklch(0.758933 0.105833 241.548)",
    primaryForeground: "oklch(0.222003 0.03479 328.979)",
    secondary: "oklch(0.358725 0.043145 244.911)",
    secondaryForeground: "oklch(0.990339 0.008411 325.64)",
    sidebar: "oklch(0.290387 0.032043 247.274)",
    sidebarBorder: "oklch(0.55859 0.019001 256.223)",
    sidebarForeground: "oklch(0.990339 0.008411 325.64)",
    warning: "oklch(0.772406 0.172798 65.367)",
    warningForeground: "oklch(0.829017 0.171221 81.038)",
  },
  description: "Deep blues with a cool teal accent.",
  id: "ocean",
  light: {
    accent: "oklch(0.91295 0.018827 241.836)",
    accentForeground: "oklch(0.222003 0.03479 328.979)",
    background: "oklch(0.974199 0.002856 241.597)",
    border: "oklch(0.867646 0.013482 252.362)",
    card: "oklch(0.951058 0.002962 258.339)",
    cardForeground: "oklch(0.222003 0.03479 328.979)",
    destructive: "oklch(0.637823 0.237287 25.436)",
    destructiveForeground: "oklch(0.509494 0.208583 28.513)",
    foreground: "oklch(0.222003 0.03479 328.979)",
    input: "oklch(0.832939 0.017389 252.598)",
    muted: "oklch(0.948004 0.009649 241.695)",
    mutedForeground: "oklch(0.528741 0.01828 313.823)",
    popover: "oklch(0.934442 0.003181 269.1)",
    popoverForeground: "oklch(0.222003 0.03479 328.979)",
    primary: "oklch(0.536684 0.120219 247.01)",
    primaryForeground: "oklch(0.990339 0.008411 325.64)",
    secondary: "oklch(0.939254 0.01193 241.729)",
    secondaryForeground: "oklch(0.222003 0.03479 328.979)",
    sidebar: "oklch(0.939254 0.01193 241.729)",
    sidebarBorder: "oklch(0.862823 0.011384 256.926)",
    sidebarForeground: "oklch(0.222003 0.03479 328.979)",
    warning: "oklch(0.772406 0.172798 65.367)",
    warningForeground: "oklch(0.546927 0.155556 45.359)",
  },
  name: "Ocean",
};

const ember: ThemePalette = {
  dark: {
    accent: "oklch(0.416048 0.055354 50.484)",
    accentForeground: "oklch(0.990339 0.008411 325.64)",
    background: "oklch(0.245899 0.019144 42.044)",
    border: "oklch(0.44099 0.040202 48.807)",
    card: "oklch(0.351262 0.01565 37.592)",
    cardForeground: "oklch(0.990339 0.008411 325.64)",
    destructive: "oklch(0.655108 0.221148 23.473)",
    destructiveForeground: "oklch(0.702184 0.189226 22.228)",
    foreground: "oklch(0.990339 0.008411 325.64)",
    input: "oklch(0.503003 0.045721 49.44)",
    muted: "oklch(0.322144 0.03574 48.309)",
    mutedForeground: "oklch(0.692479 0.015227 30.963)",
    popover: "oklch(0.401111 0.014308 34.896)",
    popoverForeground: "oklch(0.990339 0.008411 325.64)",
    primary: "oklch(0.762174 0.124117 52.082)",
    primaryForeground: "oklch(0.222003 0.03479 328.979)",
    secondary: "oklch(0.361499 0.044052 49.515)",
    secondaryForeground: "oklch(0.990339 0.008411 325.64)",
    sidebar: "oklch(0.293349 0.029554 46.882)",
    sidebarBorder: "oklch(0.560372 0.016998 36.179)",
    sidebarForeground: "oklch(0.990339 0.008411 325.64)",
    warning: "oklch(0.772406 0.172798 65.367)",
    warningForeground: "oklch(0.829017 0.171221 81.038)",
  },
  description: "Charcoal warmed by amber and rust.",
  id: "ember",
  light: {
    accent: "oklch(0.916502 0.01832 49.597)",
    accentForeground: "oklch(0.222003 0.03479 328.979)",
    background: "oklch(0.976527 0.002685 60.725)",
    border: "oklch(0.870631 0.013204 39.431)",
    card: "oklch(0.953321 0.002701 42.266)",
    cardForeground: "oklch(0.222003 0.03479 328.979)",
    destructive: "oklch(0.637823 0.237287 25.436)",
    destructiveForeground: "oklch(0.509494 0.208583 28.513)",
    foreground: "oklch(0.222003 0.03479 328.979)",
    input: "oklch(0.836213 0.017153 38.661)",
    muted: "oklch(0.950842 0.009273 51.528)",
    mutedForeground: "oklch(0.530413 0.018453 341.181)",
    popover: "oklch(0.936659 0.002879 29.96)",
    popoverForeground: "oklch(0.222003 0.03479 328.979)",
    primary: "oklch(0.552831 0.129438 44.656)",
    primaryForeground: "oklch(0.990339 0.008411 325.64)",
    secondary: "oklch(0.942267 0.01151 50.785)",
    secondaryForeground: "oklch(0.222003 0.03479 328.979)",
    sidebar: "oklch(0.942267 0.01151 50.785)",
    sidebarBorder: "oklch(0.865593 0.011154 35.246)",
    sidebarForeground: "oklch(0.222003 0.03479 328.979)",
    warning: "oklch(0.772406 0.172798 65.367)",
    warningForeground: "oklch(0.549154 0.156188 45.359)",
  },
  name: "Ember",
};

const iris: ThemePalette = {
  dark: {
    accent: "oklch(0.372436 0.07841 294.204)",
    accentForeground: "oklch(0.990339 0.008411 325.64)",
    background: "oklch(0.225975 0.031062 293.741)",
    border: "oklch(0.40874 0.058536 295.893)",
    card: "oklch(0.335291 0.026008 296.394)",
    cardForeground: "oklch(0.990339 0.008411 325.64)",
    destructive: "oklch(0.655108 0.221148 23.473)",
    destructiveForeground: "oklch(0.702184 0.189226 22.228)",
    foreground: "oklch(0.990339 0.008411 325.64)",
    input: "oklch(0.46756 0.065775 296.265)",
    muted: "oklch(0.291515 0.05276 294.209)",
    mutedForeground: "oklch(0.663321 0.025932 301.862)",
    popover: "oklch(0.386739 0.024023 297.509)",
    popoverForeground: "oklch(0.990339 0.008411 325.64)",
    primary: "oklch(0.671712 0.169136 293.929)",
    primaryForeground: "oklch(0.222003 0.03479 328.979)",
    secondary: "oklch(0.325405 0.063614 294.23)",
    secondaryForeground: "oklch(0.990339 0.008411 325.64)",
    sidebar: "oklch(0.266743 0.044689 294.138)",
    sidebarBorder: "oklch(0.545895 0.027522 299.871)",
    sidebarForeground: "oklch(0.990339 0.008411 325.64)",
    warning: "oklch(0.772406 0.172798 65.367)",
    warningForeground: "oklch(0.829017 0.171221 81.038)",
  },
  description: "Muted violets on a soft grey canvas.",
  id: "iris",
  light: {
    accent: "oklch(0.914882 0.022965 299.986)",
    accentForeground: "oklch(0.222003 0.03479 328.979)",
    background: "oklch(0.976531 0.003855 303.226)",
    border: "oklch(0.869608 0.018226 303.859)",
    card: "oklch(0.953326 0.004536 307.676)",
    cardForeground: "oklch(0.222003 0.03479 328.979)",
    destructive: "oklch(0.637823 0.237287 25.436)",
    destructiveForeground: "oklch(0.509494 0.208583 28.513)",
    foreground: "oklch(0.222003 0.03479 328.979)",
    input: "oklch(0.834773 0.023405 303.676)",
    muted: "oklch(0.950194 0.011956 300.733)",
    mutedForeground: "oklch(0.529955 0.022319 321.556)",
    popover: "oklch(0.936665 0.005041 310.132)",
    popoverForeground: "oklch(0.222003 0.03479 328.979)",
    primary: "oklch(0.525348 0.15373 294.176)",
    primaryForeground: "oklch(0.990339 0.008411 325.64)",
    secondary: "oklch(0.941387 0.014687 300.474)",
    secondaryForeground: "oklch(0.222003 0.03479 328.979)",
    sidebar: "oklch(0.941387 0.014687 300.474)",
    sidebarBorder: "oklch(0.864805 0.015938 305.371)",
    sidebarForeground: "oklch(0.222003 0.03479 328.979)",
    warning: "oklch(0.772406 0.172798 65.367)",
    warningForeground: "oklch(0.549154 0.156188 45.359)",
  },
  name: "Iris",
};

/** Every selectable palette, in picker order. */
export const THEME_PALETTES: readonly ThemePalette[] = [grove, ocean, ember, iris];

export const THEME_PALETTE_IDS: readonly string[] = THEME_PALETTES.map((palette) => palette.id);

/** `forge` plus every palette id — the accepted values for the `themeId` setting. */
export const THEME_IDS: readonly string[] = [DEFAULT_THEME_ID, ...THEME_PALETTE_IDS];

export function findThemePalette(id: string): ThemePalette | undefined {
  return THEME_PALETTES.find((palette) => palette.id === id);
}

/**
 * Unknown ids decode to the default rather than failing. A settings file
 * written by a newer build, or one naming a palette that has since been
 * removed, should cost the user their theme and nothing else.
 */
export const ThemeId = Schema.String.pipe(
  Schema.withDecodingDefault(Effect.succeed(DEFAULT_THEME_ID)),
);
